import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
const PrivacyPolicy = () => {
  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-background text-foreground">
      <SEO title="Privacy Policy | Klexu" description="How Klexu collects, processes and protects personal data, with GDPR-compliant EU data residency." path="/privacy-policy" />
      <Navbar />
      <main className="pt-24 pb-20">
        <div className="container px-4 sm:px-6 lg:px-8 mx-auto">
          <Button variant="ghost" className="mb-8 text-muted-foreground hover:text-white" onClick={() => window.history.back()}>
            <ArrowLeft size={16} className="mr-2" />
            Back
          </Button>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Privacy Policy
          </h1>

          <div className="glass-card p-8 rounded-xl mb-10">
            <div className="max-w-full space-y-8 text-justify">
              <div>
                <p className="text-sm text-muted-foreground mb-6">
                  <strong>Version:</strong> 2.1 | <strong>Last Updated:</strong> December 3, 2025 | 
                  <strong>Previous Version:</strong> December 24, 2024 (available upon request)
                </p>
                
                <div className="mb-8">
                  <h2 className="text-xl font-bold mb-3">Territorial Scope</h2>
                  <p className="text-muted-foreground mb-4">This Privacy Policy applies to all users and data subjects located in the European Economic Area (EEA) and the United States of America (USA). Klexu Resources SRL, located in European Economic Area (EEA), serves as the data controller for all data processing activities covered by this policy.</p>
                  <p className="text-muted-foreground mb-4">
                    For EEA users, this policy ensures compliance with the General Data Protection Regulation (GDPR). For USA users, this policy ensures compliance with applicable federal and state privacy laws, including but not limited to the California Consumer Privacy Act (CCPA), Virginia Consumer Data Protection Act (VCDPA), and other state privacy regulations.
                  </p>
                </div>

                <h2 className="text-xl font-bold mb-3">
                  Types of Data collected
                </h2>
                <p className="text-muted-foreground mb-4">
                  Among the types of Personal Data that this Application collects, by itself or through third parties, there are: email address; phone number; first name; last name; company name; country; ZIP/Postal code; various types of Data; city; field of activity; number of employees; website; Trackers; session duration; scroll-to-page interactions; clicks; time zone; device information; Usage Data; mouse movements; interaction events; diagnostic events; page events; custom events; layout details; positional information; calendar information; name; geographic position; answers to questions; keypress events; motion sensor events; scroll position; touch events; number of Users; session statistics.
                </p>
                <p className="text-muted-foreground mb-4">
                  Complete details on each type of Personal Data collected are provided in the dedicated sections of this privacy policy or by specific explanation texts displayed prior to the Data collection.
                  Personal Data may be freely provided by the User, or, in case of Usage Data, collected automatically when using this Application.
                  Unless specified otherwise, all Data requested by this Application is mandatory and failure to provide this Data may make it impossible for this Application to provide its services. In cases where this Application specifically states that some Data is not mandatory, Users are free not to communicate this Data without consequences to the availability or the functioning of the Service.
                  Users who are uncertain about which Personal Data is mandatory are welcome to contact the Owner.
                </p>
                <p className="text-muted-foreground mb-4">
                  Any use of Cookies – or of other tracking tools — by this Application or by the owners of third-party services used by this Application serves the purpose of providing the Service required by the User, in addition to any other purposes described in the present document and in the Cookie Policy.
                </p>
                <p className="text-muted-foreground mb-4">
                  Users are responsible for any third-party Personal Data obtained, published or shared through this Application.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-3">
                  Mode and place of processing the Data
                </h2>
                <strong className="block mb-1">Methods of processing</strong>
                <p className="text-muted-foreground mb-3">
                  The Owner takes appropriate security measures to prevent unauthorized access, disclosure, modification, or unauthorized destruction of the Data. The Data processing is carried out using computers and/or IT enabled tools, following organizational procedures and modes strictly related to the purposes indicated. In addition to the Owner, in some cases, the Data may be accessible to certain types of persons in charge, involved with the operation of this Application (administration, sales, marketing, legal, system administration) or external parties (such as third-party technical service providers, mail carriers, hosting providers, IT companies, communications agencies) appointed, if necessary, as Data Processors by the Owner. The updated list of these parties may be requested from the Owner at any time.
                </p>
                <strong className="block mb-1">Place</strong>
                <p className="text-muted-foreground mb-3">
                  The Data is processed at the Owner's operating offices and in any other places where the parties involved in the processing are located. Depending on the User's location, data transfers may involve transferring the User's Data to a country other than their own. To find out more about the place of processing of such transferred Data, Users can check the section containing details about the processing of Personal Data.
                </p>
                <strong className="block mb-1">Retention time</strong>
                <p className="text-muted-foreground mb-3">
                  Unless specified otherwise in this document, Personal Data shall be processed and stored for as long as required by the purpose they have been collected for and may be retained for longer due to applicable legal obligation or based on the Users’ consent.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-3">The purposes of processing</h2>
                <p className="text-muted-foreground mb-4">
                  The Data concerning the User is collected to allow the Owner to provide its Service, comply with its legal obligations, respond to enforcement requests, protect its rights and interests (or those of its Users or third parties), detect any malicious or fraudulent activity, as well as the following: Interaction with live chat platforms, Contacting the User, Heat mapping and session recording, Displaying content from external platforms, Analytics, Advertising, Remarketing and behavioral targeting, User database management, Hosting and backend infrastructure and Spam and bots protection.
                </p>
                <p className="text-muted-foreground mb-4">
                  For specific information about the Personal Data used for each purpose, the User may refer to the section “Detailed information on the processing of Personal Data”.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-3">Detailed information on the processing of Personal Data</h2>
                <p className="text-muted-foreground mb-2">
                  Personal Data is collected for the following purposes and using the following services:
                </p>
                <ul className="list-disc pl-6 mb-2 text-muted-foreground">
                  <li>Advertising</li>
                  <li>Analytics</li>
                  <li>Contacting the User</li>
                  <li>Displaying content from external platforms</li>
                  <li>Heat mapping and session recording</li>
                  <li>Hosting and backend infrastructure</li>
                  <li>Interaction with live chat platforms</li>
                  <li>Remarketing and behavioral targeting</li>
                  <li>Spam and bots protection</li>
                  <li>User database management</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-4">Complete List of Third-Party Data Processors</h2>
                <p className="text-muted-foreground mb-4">
                  We engage the following third-party processors to help us provide our services. All processors are contractually bound to process data only according to our instructions and maintain appropriate security measures. We review our processors annually and update this list as needed.
                </p>

                <div className="space-y-4">
                  <div className="border border-border rounded-lg p-4">
                    <h3 className="font-semibold mb-3">Infrastructure & Hosting</h3>
                    <div className="space-y-3 text-sm">
                      <div className="border-l-2 border-primary/50 pl-3">
                        <p className="font-medium text-foreground">Supabase Inc.</p>
                        <p className="text-muted-foreground">Database, authentication, storage</p>
                        <p className="text-muted-foreground"><strong>Location:</strong> United States</p>
                        <p className="text-muted-foreground"><strong>Safeguards:</strong> Standard Contractual Clauses (SCCs)</p>
                        <p className="text-muted-foreground"><strong>DPA:</strong> Available upon request</p>
                      </div>
                      <div className="border-l-2 border-primary/50 pl-3">
                        <p className="font-medium text-foreground">Vercel Inc.</p>
                        <p className="text-muted-foreground">Frontend hosting & CDN</p>
                        <p className="text-muted-foreground"><strong>Location:</strong> United States</p>
                        <p className="text-muted-foreground"><strong>Safeguards:</strong> Standard Contractual Clauses (SCCs)</p>
                        <p className="text-muted-foreground"><strong>DPA:</strong> <a href="https://vercel.com/legal/dpa" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">https://vercel.com/legal/dpa</a></p>
                      </div>
                    </div>
                  </div>

                  <div className="border border-border rounded-lg p-4">
                    <h3 className="font-semibold mb-3">AI & Machine Learning</h3>
                    <div className="space-y-3 text-sm">
                      <div className="border-l-2 border-primary/50 pl-3">
                        <p className="font-medium text-foreground">OpenAI, LLC</p>
                        <p className="text-muted-foreground">AI-powered features</p>
                        <p className="text-muted-foreground"><strong>Location:</strong> United States</p>
                        <p className="text-muted-foreground"><strong>Safeguards:</strong> Standard Contractual Clauses (SCCs)</p>
                        <p className="text-muted-foreground"><strong>DPA:</strong> <a href="https://openai.com/policies/dpa" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">https://openai.com/policies/dpa</a></p>
                      </div>
                    </div>
                  </div>

                  <div className="border border-border rounded-lg p-4">
                    <h3 className="font-semibold mb-3">Analytics & Performance Monitoring</h3>
                    <div className="space-y-3 text-sm">
                      <div className="border-l-2 border-primary/50 pl-3">
                        <p className="font-medium text-foreground">Google LLC</p>
                        <p className="text-muted-foreground">Analytics, advertising</p>
                        <p className="text-muted-foreground"><strong>Location:</strong> United States</p>
                        <p className="text-muted-foreground"><strong>Safeguards:</strong> EU-US Data Privacy Framework</p>
                        <p className="text-muted-foreground"><strong>Privacy Policy:</strong> <a href="https://policies.google.com/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a></p>
                      </div>
                      <div className="border-l-2 border-primary/50 pl-3">
                        <p className="font-medium text-foreground">Microsoft Corporation</p>
                        <p className="text-muted-foreground">Clarity heatmapping</p>
                        <p className="text-muted-foreground"><strong>Location:</strong> United States</p>
                        <p className="text-muted-foreground"><strong>Safeguards:</strong> EU-US Data Privacy Framework</p>
                        <p className="text-muted-foreground"><strong>Privacy Policy:</strong> <a href="https://privacy.microsoft.com" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">https://privacy.microsoft.com</a></p>
                      </div>
                    </div>
                  </div>

                  <div className="border border-border rounded-lg p-4">
                    <h3 className="font-semibold mb-3">CRM & Communications</h3>
                    <div className="space-y-3 text-sm">
                      <div className="border-l-2 border-primary/50 pl-3">
                        <p className="font-medium text-foreground">HubSpot Inc.</p>
                        <p className="text-muted-foreground">CRM, email, live chat</p>
                        <p className="text-muted-foreground"><strong>Location:</strong> United States</p>
                        <p className="text-muted-foreground"><strong>Safeguards:</strong> EU-US Data Privacy Framework</p>
                        <p className="text-muted-foreground"><strong>DPA:</strong> <a href="https://legal.hubspot.com/dpa" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">https://legal.hubspot.com/dpa</a></p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-4">Data Breach Notification Procedure</h2>
                <p className="text-muted-foreground mb-4">
                  In the event of a personal data breach, Klexu Resources SRL follows these procedures:
                </p>

                <div className="space-y-4">
                  <div className="border border-border rounded-lg p-4">
                    <h3 className="font-semibold mb-2">Detection & Assessment (0-24 hours)</h3>
                    <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                      <li>Immediate investigation upon breach detection</li>
                      <li>Assessment of breach scope, affected data types, and number of users</li>
                      <li>Determination of risk level to individuals' rights and freedoms</li>
                    </ul>
                  </div>

                  <div className="border border-border rounded-lg p-4">
                    <h3 className="font-semibold mb-2">Regulatory Notification (Within 72 hours)</h3>
                    <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                      <li>If high risk: Notification to Romanian supervisory authority (ANSPDCP)</li>
                      <li>Breach details: nature, categories of data, approximate number of affected individuals</li>
                      <li>Contact point: <a href="mailto:admin@klexu.com" className="text-primary hover:underline">admin@klexu.com</a></li>
                    </ul>
                  </div>

                  <div className="border border-border rounded-lg p-4">
                    <h3 className="font-semibold mb-2">User Notification (Without undue delay)</h3>
                    <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                      <li>Direct notification to affected users if high risk to their rights</li>
                      <li>Notification includes: nature of breach, likely consequences, measures taken</li>
                      <li>Communication via email to registered email address</li>
                      <li>Public notification if individual notification not feasible</li>
                    </ul>
                  </div>

                  <div className="border border-border rounded-lg p-4">
                    <h3 className="font-semibold mb-2">Remediation & Documentation</h3>
                    <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                      <li>Immediate containment and security enhancement measures</li>
                      <li>Full documentation of breach, effects, and remedial action</li>
                      <li>Post-incident review and security improvements</li>
                    </ul>
                  </div>

                  <div className="border border-border rounded-lg p-4 bg-muted/50">
                    <h3 className="font-semibold mb-2">Contact for Breach Reports</h3>
                    <p className="text-sm text-muted-foreground mb-2">
                      If you suspect a data breach or security incident, please contact us immediately:
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <strong>Email:</strong> <a href="mailto:admin@klexu.com" className="text-primary hover:underline">admin@klexu.com</a>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <strong>Response time:</strong> Within 24 hours
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-3">
                  Information on opting out of interest-based advertising
                </h2>
                <p className="text-muted-foreground mb-4">
                  In addition to any opt-out feature provided by any of the services listed in this document, Users may learn more on how to generally opt out of interest-based advertising within the dedicated section of the Cookie Policy.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-3">Cookie Policy</h2>
                <p className="text-muted-foreground mb-4">
                  This Application uses Trackers. To learn more, Users may consult the Cookie Policy.
                </p>
              </div>

              <div>
                <strong className="block mb-1">Further Information for Users</strong>
                <strong className="block mb-1">Legal basis of processing</strong>
                <p className="text-muted-foreground mb-3">
                  The Owner may process Personal Data relating to Users if one of the following applies:
                </p>
                <ul className="list-disc pl-6 mb-3 text-muted-foreground">
                  <li>Users have given their consent for one or more specific purposes.</li>
                  <li>Provision of Data is necessary for the performance of an agreement with the User and/or for any pre-contractual obligations thereof;</li>
                  <li>Processing is necessary for compliance with a legal obligation to which the Owner is subject;</li>
                  <li>Processing is related to a task that is carried out in the public interest or in the exercise of official authority vested in the Owner;</li>
                  <li>Processing is necessary for the purposes of the legitimate interests pursued by the Owner or by a third party.</li>
                </ul>
                <p className="text-muted-foreground mb-3">
                  In any case, the Owner will gladly help to clarify the specific legal basis that applies to the processing, and in particular whether the provision of Personal Data is a statutory or contractual requirement, or a requirement necessary to enter into a contract.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-4">Specific Legal Basis for Each Processing Activity</h2>
                <p className="text-muted-foreground mb-4">
                  We process different types of personal data under different legal bases as outlined below:
                </p>

                <div className="border border-border rounded-lg p-4 mb-4">
                  <h3 className="font-semibold mb-2">Account & Profile Data</h3>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p><strong>Data:</strong> Email, Name, Phone, Company</p>
                    <p><strong>Legal Basis:</strong> Contract Performance (GDPR Art. 6(1)(b))</p>
                    <p><strong>Purpose:</strong> To provide account access and service delivery</p>
                    <p><strong>Retention:</strong> Until account deletion + 30 days for cleanup</p>
                  </div>
                </div>

                <div className="border border-border rounded-lg p-4 mb-4">
                  <h3 className="font-semibold mb-2">Assessment & Usage Data</h3>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p><strong>Data:</strong> Assessment responses, preferences, interaction data</p>
                    <p><strong>Legal Basis:</strong> Legitimate Interest (GDPR Art. 6(1)(f))</p>
                    <p><strong>Purpose:</strong> Service improvement and personalized recommendations</p>
                    <p><strong>Retention:</strong> 24 months from last account activity</p>
                  </div>
                </div>

                <div className="border border-border rounded-lg p-4 mb-4">
                  <h3 className="font-semibold mb-2">Marketing Communications</h3>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p><strong>Data:</strong> Email, communication preferences</p>
                    <p><strong>Legal Basis:</strong> Consent (GDPR Art. 6(1)(a))</p>
                    <p><strong>Purpose:</strong> Newsletter, promotional materials</p>
                    <p><strong>Retention:</strong> Until consent withdrawn + 30 days</p>
                  </div>
                </div>

                <div className="border border-border rounded-lg p-4 mb-4">
                  <h3 className="font-semibold mb-2">Security & Fraud Prevention</h3>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p><strong>Data:</strong> IP addresses, login attempts, device fingerprints</p>
                    <p><strong>Legal Basis:</strong> Legitimate Interest (GDPR Art. 6(1)(f))</p>
                    <p><strong>Purpose:</strong> Security monitoring and fraud prevention</p>
                    <p><strong>Retention:</strong> 12 months from collection</p>
                  </div>
                </div>

                <div className="border border-border rounded-lg p-4 mb-4">
                  <h3 className="font-semibold mb-2">Contact Form Submissions</h3>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p><strong>Data:</strong> Name, email, company, message content</p>
                    <p><strong>Legal Basis:</strong> Legitimate Interest (GDPR Art. 6(1)(f))</p>
                    <p><strong>Purpose:</strong> Respond to inquiries and business development</p>
                    <p><strong>Retention:</strong> 36 months or until request resolved</p>
                  </div>
                </div>

                <div className="border border-border rounded-lg p-4 mb-4">
                  <h3 className="font-semibold mb-2">Legal Compliance & Accounting</h3>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p><strong>Data:</strong> Transaction records, contracts, invoices</p>
                    <p><strong>Legal Basis:</strong> Legal Obligation (GDPR Art. 6(1)(c))</p>
                    <p><strong>Purpose:</strong> Tax compliance, legal requirements</p>
                    <p><strong>Retention:</strong> 7 years (Romanian legal requirement)</p>
                  </div>
                </div>
              </div>

              <div>
                <strong className="block mb-1">Further information about retention time</strong>
                <p className="text-muted-foreground mb-3">
                  Unless specified otherwise in this document, Personal Data shall be processed and stored for as long as required by the purpose they have been collected for and may be retained for longer due to applicable legal obligation or based on the Users’ consent.
                </p>
                <p className="text-muted-foreground mb-3">
                  Therefore:
                </p>
                <ul className="list-disc pl-6 mb-3 text-muted-foreground">
                  <li>
                    Personal Data collected for purposes related to the performance of a contract between the Owner and the User shall be retained until such contract has been fully performed.
                  </li>
                  <li>
                    Personal Data collected for the purposes of the Owner’s legitimate interests shall be retained as long as needed to fulfill such purposes. Users may find specific information regarding the legitimate interests pursued by the Owner within the relevant sections of this document or by contacting the Owner.
                  </li>
                  <li>
                    The Owner may be allowed to retain Personal Data for a longer period whenever the User has given consent to such processing, as long as such consent is not withdrawn. Furthermore, the Owner may be obliged to retain Personal Data for a longer period whenever required to fulfil a legal obligation or upon order of an authority.
                  </li>
                </ul>
                <p className="text-muted-foreground mb-3">
                  Once the retention period expires, Personal Data shall be deleted. Therefore, the right of access, the right to erasure, the right to rectification and the right to data portability cannot be enforced after expiration of the retention period.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-3">
                  The rights of Users based on the General Data Protection Regulation (GDPR)
                </h2>
                <p className="text-muted-foreground mb-4">
                  Users may exercise certain rights regarding their Data processed by the Owner. In particular, Users have the right to do the following, to the extent permitted by law:
                </p>
                <ul className="list-disc pl-6 mb-3 text-muted-foreground">
                  <li>Withdraw their consent at any time. Users have the right to withdraw consent where they have previously given their consent to the processing of their Personal Data.</li>
                  <li>Object to processing of their Data. Users have the right to object to the processing of their Data if the processing is carried out on a legal basis other than consent.</li>
                  <li>Access their Data. Users have the right to learn if Data is being processed by the Owner, obtain disclosure regarding certain aspects of the processing and obtain a copy of the Data undergoing processing.</li>
                  <li>Verify and seek rectification. Users have the right to verify the accuracy of their Data and ask for it to be updated or corrected.</li>
                  <li>Restrict the processing of their Data. Users have the right to restrict the processing of their Data. In this case, the Owner will not process their Data for any purpose other than storing it.</li>
                  <li>Have their Personal Data deleted or otherwise removed. Users have the right to obtain the erasure of their Data from the Owner.</li>
                  <li>Receive their Data and have it transferred to another controller. Users have the right to receive their Data in a structured, commonly used and machine readable format and, if technically feasible, to have it transmitted to another controller without any hindrance.</li>
                  <li>Lodge a complaint. Users have the right to bring a claim before their competent data protection authority.</li>
                </ul>
                <p className="text-muted-foreground mb-3">
                  Users are also entitled to learn about the legal basis for Data transfers abroad including to any international organization governed by public international law or set up by two or more countries, such as the UN, and about the security measures taken by the Owner to safeguard their Data.
                </p>

                <strong className="block mb-1">Details about the right to object to processing</strong>
                <p className="text-muted-foreground mb-3">
                  Where Personal Data is processed for a public interest, in the exercise of an official authority vested in the Owner or for the purposes of the legitimate interests pursued by the Owner, Users may object to such processing by providing a ground related to their particular situation to justify the objection.
                </p>
                <p className="text-muted-foreground mb-3">
                  Users must know that, however, should their Personal Data be processed for direct marketing purposes, they can object to that processing at any time, free of charge and without providing any justification. Where the User objects to processing for direct marketing purposes, the Personal Data will no longer be processed for such purposes. To learn whether the Owner is processing Personal Data for direct marketing purposes, Users may refer to the relevant sections of this document.
                </p>

                <strong className="block mb-1">How to exercise these rights</strong>
                <p className="text-muted-foreground mb-3">
                  Any requests to exercise User rights can be directed to the Owner through the contact details provided in this document. Such requests are free of charge and will be answered by the Owner as early as possible and always within one month, providing Users with the information required by law. Any rectification or erasure of Personal Data or restriction of processing will be communicated by the Owner to each recipient, if any, to whom the Personal Data has been disclosed unless this proves impossible or involves disproportionate effort. At the Users’ request, the Owner will inform them about those recipients.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-3">
                  Further information for Users in Switzerland
                </h2>
                <p className="text-muted-foreground mb-4">
                  This section applies to Users in Switzerland, and, for such Users, supersedes any other possibly divergent or conflicting information contained in the privacy policy.
                </p>
                <p className="text-muted-foreground mb-4">
                  Further details regarding the categories of Data processed, the purposes of processing, the categories of recipients of the personal data, if any, the retention period and further information about Personal Data can be found in the section titled “Detailed information on the processing of Personal Data” within this document.
                </p>
                <strong className="block mb-1">
                  The rights of Users according to the Swiss Federal Act on Data Protection
                </strong>
                <ul className="list-disc pl-6 mb-3 text-muted-foreground">
                  <li>right of access to Personal Data;</li>
                  <li>right to object to the processing of their Personal Data (which also allows Users to demand that processing of Personal Data be restricted, Personal Data be deleted or destroyed, specific disclosures of Personal Data to third parties be prohibited);</li>
                  <li>right to receive their Personal Data and have it transferred to another controller (data portability);</li>
                  <li>right to ask for incorrect Personal Data to be corrected.</li>
                </ul>
                <strong className="block mb-1">How to exercise these rights</strong>
                <p className="text-muted-foreground mb-3">
                  Any requests to exercise User rights can be directed to the Owner through the contact details provided in this document. Such requests are free of charge and will be answered by the Owner as early as possible, providing Users with the information required by law.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-3">
                  Further information for Users in the United States
                </h2>
                <p className="text-muted-foreground mb-4">
                  This part of the document integrates with and supplements the information contained in the rest of the privacy policy and is provided by the business running this Application and, if the case may be, its parent, subsidiaries and affiliates (for the purposes of this section referred to collectively as “we”, “us”, “our”).
                </p>
                <p className="text-muted-foreground mb-4">
                  The information contained in this section applies to all Users (Users are referred to below, simply as “you”, “your”, “yours”), who are residents in the following states: California, Virginia, Colorado, Connecticut, Utah, Texas, Oregon, Nevada, Delaware, Iowa, New Hampshire, New Jersey, Nebraska and Montana.
                </p>
                <p className="text-muted-foreground mb-4">
                  For such Users, this information supersedes any other possibly divergent or conflicting provisions contained in the privacy policy.
                </p>
                <p className="text-muted-foreground mb-2 font-semibold">Notice at collection</p>
                <p className="text-muted-foreground mb-4">
                  The following Notice at collection provides you with timely notice about the categories of Personal Information collected or disclosed in the past 12 months so that you can exercise meaningful control over our use of that Information.
                </p>
                <ul className="list-disc pl-6 mb-3 text-muted-foreground">
                  <li>Identifiers</li>
                  <li>Commercial information</li>
                  <li>Internet or other electronic network activity information</li>
                  <li>Employment related information</li>
                  <li>Geolocation data</li>
                  <li>Inferences drawn from other personal information</li>
                </ul>
                <p className="text-sm text-muted-foreground mb-3">
                  ℹ️ You can read the definitions of these concepts inside the “Definitions and legal references section” of the privacy policy.
                </p>
                <p className="text-muted-foreground mb-3">
                  To know more about your rights in particular to opt out of certain processing activities you can refer to the “Your privacy rights under US state laws” section of our privacy policy.
                </p>
                <p className="text-muted-foreground mb-3">
                  For more details on the collection of Personal Information, please read the section “Detailed information on the processing of Personal Data” of our privacy policy.
                </p>
                <p className="text-muted-foreground mb-3">
                  We won’t process your Information for unexpected purposes, or for purposes that are not reasonably necessary to and compatible with the purposes originally disclosed, without your consent.
                </p>
                <strong className="block mb-1">
                  What are the sources of the Personal Information we collect?
                </strong>
                <p className="text-muted-foreground mb-3">
                  We collect the above-mentioned categories of Personal Information, either directly or indirectly, from you when you use this Application.
                </p>
                <p className="text-muted-foreground mb-3">
                  For example, you directly provide your Personal Information when you submit requests via any forms on this Application. You also provide Personal Information indirectly when you navigate this Application, as Personal Information about you is automatically observed and collected.
                </p>
                <p className="text-muted-foreground mb-3">
                  Finally, we may collect your Personal Information from third parties that work with us in connection with the Service or with the functioning of this Application and features thereof.
                </p>
                <h3 className="font-semibold mb-2">
                  Your privacy rights under US state laws
                </h3>
                <ul className="list-disc pl-6 mb-3 text-muted-foreground">
                  <li>the right to access Personal Information: the right to know.</li>
                  <li>the right to correct inaccurate Personal Information.</li>
                  <li>the right to request the deletion of your Personal Information.</li>
                  <li>the right to obtain a copy of your Personal Information.</li>
                  <li>the right to opt out from the Sale of your Personal Information; We will not discriminate against you for exercising your privacy rights.</li>
                  <li>the right to non-discrimination.</li>
                </ul>

                <strong className="block mb-1">Additional rights for California, Virginia, etc.</strong>
                <ul className="list-disc pl-6 mb-3 text-muted-foreground">
                  <li>
                    The right to opt out of the Sharing of your Personal Information for cross-context behavioral advertising;
                  </li>
                  <li>
                    The right to request to limit our use or disclosure of your Sensitive Personal Information to only that which is necessary to perform the services or provide the goods, as is reasonably expected by an average consumer.
                  </li>
                </ul>
                <strong className="block mb-1">Additional rights for Users residing in Virginia, Colorado, Connecticut, Texas, Oregon, Nevada, Delaware, Iowa, New Hampshire, New Jersey, Nebraska and Montana</strong>
                <ul className="list-disc pl-6 mb-3 text-muted-foreground">
                  <li>
                    The right to opt out of the processing of your personal information for Targeted Advertising or profiling in furtherance of decisions that produce legal or similarly significant effects concerning you;
                  </li>
                  <li>
                    The right to freely give, deny or withdraw your consent for the processing of your Sensitive Personal Information.
                  </li>
                </ul>
                <strong className="block mb-1">Additional rights for users residing in Utah and Iowa</strong>
                <ul className="list-disc pl-6 mb-3 text-muted-foreground">
                  <li>
                    The right to opt out of the processing of your Personal Information for Targeted Advertising;
                  </li>
                  <li>
                    The right to opt out of the processing of your Sensitive Personal Information.
                  </li>
                </ul>
                <strong className="block mb-1">How to exercise your privacy rights under US state laws</strong>
                <p className="text-muted-foreground mb-3">
                  To exercise the rights described above, you need to submit your request to us by contacting us via the contact details provided in this document.
                </p>
                <p className="text-muted-foreground mb-3">
                  For us to respond to your request, we must know who you are. We will not respond to any request if we are unable to verify your identity and therefore confirm the Personal Information in our possession relates to you. You are not required to create an account with us to submit your request. We will use any Personal Information collected from you in connection with the verification of your request solely for verification and shall not further disclose the Personal Information, retain it longer than necessary for purposes of verification, or use it for unrelated purposes.
                </p>
                <p className="text-muted-foreground mb-3">
                  If you are an adult, you can make a request on behalf of a child under your parental authority.
                </p>
                <strong className="block mb-1">How to exercise your rights to opt out</strong>
                <p className="text-muted-foreground mb-3">
                  In addition to what is stated above, to exercise your right to opt-out of Sale or Sharing and Targeted Advertising you can also use the privacy choices link provided on this Application.
                </p>
                <p className="text-muted-foreground mb-3">
                  If you want to submit requests to opt out of Sale or Sharing and Targeted Advertising activities via a user-enabled global privacy control, such as for example the Global Privacy Control (“GPC”), you are free to do so and we will abide by such request in a frictionless manner.
                </p>
                <strong className="block mb-1">How and when we are expected to handle your request</strong>
                <p className="text-muted-foreground mb-3">
                  We will respond to your request without undue delay, but in all cases within the timeframe required by applicable law. Should we need more time, we will explain to you the reasons why, and how much more time we need.
                </p>
                <p className="text-muted-foreground mb-3">
                  Should we deny your request, we will explain to you the reasons behind our denial (where envisaged by applicable law you may then contact the relevant authority to submit a complaint).
                </p>
                <p className="text-muted-foreground mb-3">
                  We do not charge a fee to process or respond to your request unless such request is manifestly unfounded or excessive and in all other cases where it is permitted by the applicable law. In such cases, we may charge a reasonable fee or refuse to act on the request. In either case, we will communicate our choices and explain the reasons behind them.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-3">
                  Personal Data processed for the following purposes and using the following services:
                </h2>

                <div className="mb-4">
                  <h3 className="font-semibold mb-2">Advertising</h3>
                  <ul className="list-disc pl-6 mb-2 text-muted-foreground">
                    <li>
                      <strong>LinkedIn conversion tracking (LinkedIn Insight Tag)</strong>
                      <br />
                      Personal Data: device information; Trackers; Usage Data
                    </li>
                    <li>
                      <strong>Google Ads conversion tracking</strong>
                      <br />
                      Personal Data: Trackers; Usage Data
                    </li>
                  </ul>
                </div>

                <div className="mb-4">
                  <h3 className="font-semibold mb-2">Analytics</h3>
                  <ul className="list-disc pl-6 mb-2 text-muted-foreground">
                    <li>
                      <strong>Google Analytics (Universal Analytics) with anonymized IP and HubSpot Analytics</strong>
                      <br />
                      Personal Data: Trackers; Usage Data
                    </li>
                    <li>
                      <strong>Google Analytics 4</strong>
                      <br />
                      Personal Data: number of Users; session statistics; Trackers; Usage Data
                    </li>
                  </ul>
                </div>

                <div className="mb-4">
                  <h3 className="font-semibold mb-2">Contacting the User</h3>
                  <ul className="list-disc pl-6 mb-2 text-muted-foreground">
                    <li>
                      <strong>Contact form</strong>
                      <br />
                      Personal Data: city; company name; country; email address; field of activity; first name; last name; number of employees; phone number; various types of Data; website; ZIP/Postal code
                    </li>
                  </ul>
                </div>

                <div className="mb-4">
                  <h3 className="font-semibold mb-2">Displaying content from external platforms</h3>
                  <ul className="list-disc pl-6 mb-2 text-muted-foreground">
                    <li>
                      <strong>Calendly widget</strong>
                      <br />
                      Personal Data: calendar information; email address; geographic position; name; phone number; Trackers
                    </li>
                    <li>
                      <strong>Google Fonts and Font Awesome</strong>
                      <br />
                      Personal Data: Trackers; Usage Data
                    </li>
                  </ul>
                </div>

                <div className="mb-4">
                  <h3 className="font-semibold mb-2">Heat mapping and session recording</h3>
                  <ul className="list-disc pl-6 mb-2 text-muted-foreground">
                    <li>
                      <strong>Microsoft Clarity</strong>
                      <br />
                      Personal Data: clicks; country; custom events; device information; diagnostic events; interaction events; layout details; mouse movements; page events; positional information; scroll-to-page interactions; session duration; time zone; Trackers; Usage Data
                    </li>
                  </ul>
                </div>

                <div className="mb-4">
                  <h3 className="font-semibold mb-2">Hosting and backend infrastructure</h3>
                  <ul className="list-disc pl-6 mb-2 text-muted-foreground">
                    <li>
                      <strong>OpenAI API</strong>
                      <br />
                      Personal Data: company name; device information
                    </li>
                  </ul>
                </div>

                <div className="mb-4">
                  <h3 className="font-semibold mb-2">Interaction with live chat platforms</h3>
                  <ul className="list-disc pl-6 mb-2 text-muted-foreground">
                    <li>
                      <strong>HubSpot Chat</strong>
                      <br />
                      Personal Data: email address; phone number
                    </li>
                  </ul>
                </div>

                <div className="mb-4">
                  <h3 className="font-semibold mb-2">Remarketing and behavioral targeting</h3>
                  <ul className="list-disc pl-6 mb-2 text-muted-foreground">
                    <li>
                      <strong>Google Ads Remarketing</strong>
                      <br />
                      Personal Data: Trackers; Usage Data
                    </li>
                  </ul>
                </div>

                <div className="mb-4">
                  <h3 className="font-semibold mb-2">Spam and bots protection</h3>
                  <ul className="list-disc pl-6 mb-2 text-muted-foreground">
                    <li>
                      <strong>Google reCAPTCHA</strong>
                      <br />
                      Personal Data: answers to questions; clicks; keypress events; motion sensor events; mouse movements; scroll position; touch events; Trackers; Usage Data
                    </li>
                  </ul>
                </div>

                <div className="mb-6">
                  <h3 className="font-semibold mb-2">User database management</h3>
                  <ul className="list-disc pl-6 mb-2 text-muted-foreground">
                    <li>
                      <strong>HubSpot CRM</strong>
                      <br />
                      Personal Data: company name; first name; last name; phone number
                    </li>
                  </ul>
                </div>

                <div className="mb-6">
                  <h3 className="font-semibold mb-2">Information on opting out of interest-based advertising</h3>
                  <p className="text-muted-foreground mb-4">
                    In addition to any opt-out feature provided by any of the services listed in this document, Users may learn more on how to generally opt out of interest-based advertising within the dedicated section of the Cookie Policy.
                  </p>
                </div>

                <div className="mb-6">
                  <h3 className="font-semibold mb-2">Governing Law and Jurisdiction</h3>
                  <p className="text-muted-foreground mb-4">
                    This Privacy Policy and all data processing activities are governed by Romanian law. Any disputes relating to data protection shall be submitted to the competent courts of Cluj-Napoca, Romania.
                  </p>
                </div>

                <div className="mb-6">
                  <h3 className="font-semibold mb-2">Cross-Border Data Transfers</h3>
                  <p className="text-muted-foreground mb-4">
                    When processing data from EEA users in the USA or vice versa, we ensure adequate safeguards are in place, including Standard Contractual Clauses (SCCs) and compliance with applicable data transfer mechanisms under GDPR and US privacy laws.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-3">Additional information about Data collection and processing</h2>
                <strong className="block mb-1">Legal action</strong>
                <p className="text-muted-foreground mb-3">
                  The User's Personal Data may be used for legal purposes by the Owner in Court or in the stages leading to possible legal action arising from improper use of this Application or the related Services. The User declares to be aware that the Owner may be required to reveal personal data upon request of public authorities.
                </p>
                <strong className="block mb-1">Additional information about User's Personal Data</strong>
                <p className="text-muted-foreground mb-3">
                  In addition to the information contained in this privacy policy, this Application may provide the User with additional and contextual information concerning particular Services or the collection and processing of Personal Data upon request.
                </p>
                <strong className="block mb-1">System logs and maintenance</strong>
                <p className="text-muted-foreground mb-3">
                  For operation and maintenance purposes, this Application and any third-party services may collect files that record interaction with this Application (System logs) or use other Personal Data (such as the IP Address) for this purpose.
                </p>
                <strong className="block mb-1">Information not contained in this policy</strong>
                <p className="text-muted-foreground mb-3">
                  More details concerning the collection or processing of Personal Data may be requested from the Owner at any time. Please see the contact information at the beginning of this document.
                </p>
                <strong className="block mb-1">Changes to this privacy policy</strong>
                <p className="text-muted-foreground mb-3">
                  The Owner reserves the right to make changes to this privacy policy at any time by notifying its Users on this page and possibly within this Application and/or - as far as technically and legally feasible - sending a notice to Users via any contact information available to the Owner. It is strongly recommended to check this page often, referring to the date of the last modification listed at the bottom.
                </p>
                <p className="text-muted-foreground mb-3">
                  Should the changes affect processing activities performed on the basis of the User’s consent, the Owner shall collect new consent from the User, where required.
                 </p>
                 
                 <strong className="block mb-1">Contact information and Data Controller</strong>
                 
                 
                 <p className="text-muted-foreground mb-3">
                   <br />
                   Email: <a href="mailto:admin@klexu.com" className="text-primary hover:underline">admin@klexu.com</a>
                 </p>
               </div>

               <div className="pt-8 mt-8 border-t border-border">
                 <h2 className="text-xl font-bold mb-4">Privacy Policy Changelog</h2>
                 
                 <div className="space-y-4">
                   <div>
                     <h3 className="font-semibold mb-2">Version 2.1 (December 3, 2025)</h3>
                     <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                       <li>Updated policy dates and version references</li>
                       <li>Improved changelog structure and formatting</li>
                     </ul>
                   </div>
                   
                   <div>
                     <h3 className="font-semibold mb-2">Version 2.0 (December 24, 2024)</h3>
                     <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                       <li>Added specific legal basis for each processing activity</li>
                       <li>Added complete list of third-party data processors with safeguards</li>
                       <li>Added data breach notification procedure</li>
                       <li>Added specific retention periods for all data types</li>
                       <li>Enhanced third-party processor disclosure with DPA links</li>
                       <li>Added contact information for security and privacy matters</li>
                     </ul>
                   </div>
                   
                   <div>
                     <h3 className="font-semibold mb-2">Version 1.0 (August 4, 2024)</h3>
                     <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                       <li>Initial policy publication</li>
                     </ul>
                   </div>
                  </div>
                </div>
              </div>
            </div>


          
        </div>
      </main>
      <Footer />
    </div>;
};
export default PrivacyPolicy;